import { api } from './api'
import { ANLEITUNGEN, wegschritte } from './Anleitungen'
import type { Schritt, Weg, Zaehler } from './Anleitungen'

/**
 * Die Liste des Begleiters — was sie ist, wo sie liegt und woran sie erkennt,
 * dass ein Schritt getan ist (7.1 Abschnitt 10, R545–R548).
 *
 * Alles hier ist ohne Bildschirm prüfbar: Das Bauteil `Begleiter.tsx` zeichnet
 * nur, was diese Funktionen sagen.
 */

/** Der Schlüssel im Browserspeicher (R548). Ein Wert, keine Tabelle. */
export const SPEICHER = 'coko.begleiter'

/** Ein Schritt in der Liste — Anleitung und Kennung, mehr braucht er nicht. */
export type Listenschritt = { anleitung: string; schritt: string }

/** Der Stand der Zähler, so weit die Schnittstelle sie geliefert hat. */
export type Zaehlerstand = Partial<Record<Zaehler, number>>

export type Begleitstand = {
  /** Wann die Liste angelegt wurde — Auskunft, keine Steuerung. */
  angelegt: string
  schritte: Listenschritt[]
  /** Von Hand abgehakt, als Schlüssel `anleitung/schritt`. */
  erledigt: string[]
  /** Der Stand der Zähler beim Anlegen (R546) — dagegen wird gemessen. */
  ausgangslage: Zaehlerstand
}

export function schluessel(schritt: Listenschritt): string {
  return `${schritt.anleitung}/${schritt.schritt}`
}

/** Der Schritt hinter einem Listeneintrag — `null`, wenn die Software ihn nicht mehr kennt. */
export function schrittVon(eintrag: Listenschritt): Schritt | null {
  return ANLEITUNGEN[eintrag.anleitung]?.schritte.find((s) => s.id === eintrag.schritt) ?? null
}

/**
 * Liest die Liste aus dem Browser.
 *
 * **Was nicht lesbar ist, ist keine Liste.** Ein fremder oder halber Wert im
 * Speicher wird wie kein Wert behandelt — der Begleiter fängt dann leer an,
 * statt mit einem Absturz.
 */
export function leseStand(): Begleitstand | null {
  try {
    const roh = window.localStorage.getItem(SPEICHER)
    if (!roh) return null
    const wert = JSON.parse(roh) as Partial<Begleitstand>
    if (!Array.isArray(wert.schritte) || !Array.isArray(wert.erledigt)) return null
    return {
      angelegt: typeof wert.angelegt === 'string' ? wert.angelegt : new Date().toISOString(),
      schritte: wert.schritte,
      erledigt: wert.erledigt,
      ausgangslage: wert.ausgangslage ?? {},
    }
  } catch {
    return null
  }
}

/** Schreibt die Liste — `null` nimmt sie weg (R548). */
export function schreibeStand(stand: Begleitstand | null): void {
  try {
    if (stand) {
      window.localStorage.setItem(SPEICHER, JSON.stringify(stand))
    } else {
      window.localStorage.removeItem(SPEICHER)
    }
  } catch {
    // Kein Speicher — die Liste lebt dann nur, solange das Fenster offen ist.
  }
}

/**
 * Hängt einen Weg an die Liste an — oder legt sie damit an.
 *
 * **Ein Schritt steht höchstens einmal** (R545): Wer denselben Weg zweimal
 * anhängt, bekommt ihn einmal. Die Ausgangslage wird nur für Zähler gesetzt,
 * die noch keine haben — ein Schritt, der schon abgehakt ist, behält den
 * Stand, gegen den er gemessen wurde.
 */
export function anhaengen(
  stand: Begleitstand | null,
  weg: Weg,
  lage: Zaehlerstand,
  jetzt: Date = new Date(),
): Begleitstand {
  const bisher = stand ?? { angelegt: jetzt.toISOString(), schritte: [], erledigt: [], ausgangslage: {} }
  const vorhanden = new Set(bisher.schritte.map(schluessel))
  const neue: Listenschritt[] = []

  for (const { anleitung, schritt } of wegschritte(weg)) {
    const eintrag = { anleitung, schritt: schritt.id }
    if (vorhanden.has(schluessel(eintrag))) continue
    vorhanden.add(schluessel(eintrag))
    neue.push(eintrag)
  }

  const ausgangslage: Zaehlerstand = { ...bisher.ausgangslage }
  for (const eintrag of neue) {
    const zaehler = schrittVon(eintrag)?.zaehler
    if (zaehler && ausgangslage[zaehler] === undefined && lage[zaehler] !== undefined) {
      ausgangslage[zaehler] = lage[zaehler]
    }
  }

  return { ...bisher, schritte: [...bisher.schritte, ...neue], ausgangslage }
}

/** Hakt von Hand ab — oder nimmt das Häkchen zurück. */
export function abhaken(stand: Begleitstand, eintrag: Listenschritt, erledigt: boolean): Begleitstand {
  const key = schluessel(eintrag)
  const ohne = stand.erledigt.filter((e) => e !== key)
  return { ...stand, erledigt: erledigt ? [...ohne, key] : ohne }
}

/**
 * Ob das Modell den Schritt als getan erkennt (R546).
 *
 * Erkannt heißt: Der Zähler des Schritts ist seit dem Anlegen gestiegen. Fehlt
 * die Ausgangslage oder der heutige Stand, erkennt das Modell nichts — es
 * rät nie.
 */
export function vomModellErkannt(stand: Begleitstand, schritt: Schritt, heute: Zaehlerstand): boolean {
  if (!schritt.zaehler) return false
  const vorher = stand.ausgangslage[schritt.zaehler]
  const jetzt = heute[schritt.zaehler]
  return vorher !== undefined && jetzt !== undefined && jetzt > vorher
}

export function istErledigt(
  stand: Begleitstand,
  eintrag: Listenschritt,
  schritt: Schritt | null,
  heute: Zaehlerstand,
): boolean {
  if (schritt && vomModellErkannt(stand, schritt, heute)) return true
  return stand.erledigt.includes(schluessel(eintrag))
}

/**
 * Holt den Stand aller Zähler — jeden für sich, damit ein Ausfall nicht alle
 * mitnimmt. Was nicht kommt, bleibt weg, und der Schritt bleibt beim Anwender.
 */
export async function zaehlerstand(): Promise<Zaehlerstand> {
  const zaehle = async (name: Zaehler, hole: () => Promise<number>): Promise<[Zaehler, number] | null> => {
    try {
      return [name, await hole()]
    } catch {
      return null
    }
  }

  const liste = (objektart: string) => async () => (await api.stammdatenliste(objektart)).eintraege.length

  const ergebnisse = await Promise.all([
    zaehle('einheiten', liste('einheit')),
    zaehle('beteiligungen', liste('beteiligung')),
    zaehle('kreiszugehoerigkeiten', liste('kreis_einheit')),
    zaehle('konten', liste('konto')),
    zaehle('kontozuordnungen', liste('kontozuordnung')),
    zaehle('datenquellen', async () => (await api.datenquellen()).length),
    zaehle(
      'quellenVollstaendig',
      async () =>
        (await api.datenquellen()).filter((q) => q.abbildungJson !== null && q.stand?.offen === 0).length,
    ),
    zaehle('zuordnungen', async () => (await api.kontenmapping()).length),
    // Nicht die Vorgabe von 25: Ein Zähler, der bei 25 stehen bleibt, kann
    // nicht mehr steigen — und genau das Steigen ist der Beweis.
    zaehle(
      'uebernahmen',
      async () => (await api.importlaeufe(200)).filter((lauf) => lauf.status === 'uebernommen').length,
    ),
  ])

  const stand: Zaehlerstand = {}
  for (const paar of ergebnisse) {
    if (paar) stand[paar[0]] = paar[1]
  }
  return stand
}

// ---------------------------------------------------------------------------
// Der Sprung ins Hauptfenster (R547)
// ---------------------------------------------------------------------------

const KANAL = 'coko.sprung'

export type Sprungziel = { bereich: string; unterpunkt?: string }

type Nachricht =
  | { art: 'sprung'; id: string; ziel: Sprungziel }
  | { art: 'angekommen'; id: string }

function kanal(): BroadcastChannel | null {
  return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(KANAL)
}

/**
 * Schickt den Sprung an die anderen Fenster — und springt selbst, wenn
 * keines antwortet.
 *
 * **Die Antwort ist der Beweis, dass jemand da ist.** Ohne sie wüsste der
 * Begleiter nicht, ob ein Hauptfenster offen ist, und ein Klick, der nichts
 * tut, ist keine Antwort (R145). Die Frist ist kurz: Ein Fenster auf
 * demselben Rechner antwortet in Millisekunden, und wer länger wartet, hat
 * im eigenen Fenster längst gesehen, dass nichts passiert.
 */
export function sprungSenden(ziel: Sprungziel, sonst: () => void, frist = 400): void {
  const k = kanal()
  if (!k) {
    sonst()
    return
  }

  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`
  let erledigt = false

  const schliesse = () => {
    if (erledigt) return
    erledigt = true
    window.clearTimeout(uhr)
    k.close()
  }

  k.onmessage = (ereignis: MessageEvent<Nachricht>) => {
    if (ereignis.data?.art === 'angekommen' && ereignis.data.id === id) schliesse()
  }

  const uhr = window.setTimeout(() => {
    schliesse()
    sonst()
  }, frist)

  k.postMessage({ art: 'sprung', id, ziel } satisfies Nachricht)
}

/**
 * Nimmt Sprünge entgegen. `annehmen` sagt, ob dieses Fenster springt — der
 * Begleiter selbst tut es nicht —, und nur wer springt, meldet sich zurück.
 * Gibt die Abmeldung zurück.
 */
export function sprungEmpfangen(annehmen: (ziel: Sprungziel) => boolean): () => void {
  const k = kanal()
  if (!k) return () => undefined

  k.onmessage = (ereignis: MessageEvent<Nachricht>) => {
    const nachricht = ereignis.data
    if (nachricht?.art !== 'sprung') return
    if (annehmen(nachricht.ziel)) {
      k.postMessage({ art: 'angekommen', id: nachricht.id } satisfies Nachricht)
    }
  }

  return () => k.close()
}
